/* ============================================================================
   LX NOTIFY — custom back-in-stock modal (Build A).
   ----------------------------------------------------------------------------
   A self-contained "Notify me when available" dialog that REPLACES the
   Peppyduck app UI. The backend is a documented STUB: on submit we POST to the
   endpoint configured in [data-lx-notify-config] (data-lx-notify-endpoint /
   .endpoint). When that endpoint is EMPTY (the default) we do NOT persist —
   we console.warn and still show the success state so the flow is complete and
   demoable pending the owner's real back-in-stock backend.

   Public API (product-agnostic — callable for ANY product/variant):
       window.LxNotify.open({ productTitle, variantTitle, productId, variantId })

   Mounting: the <dialog> DOM is built once on the FIRST open() (zero DOM cost
   while closed), appended to <body>, and reused across opens. The script is
   loaded by snippets/lx-notify.liquid on the product page, and lazy-injected
   by assets/lx-quickshop.js on card pages (it derives our asset URL from its
   own <script src>, so no Liquid change to the quickshop snippet is needed).

   i18n: labels come from the [data-lx-notify-config] JSON blob (rendered by the
   snippet, translated server-side via locale files). On pages without the blob
   (card pages reached through quickshop) we fall back to the built-in EN / AR
   dictionaries picked by <html dir>/<html lang>. Phone + country stay LTR so
   western digits read correctly on /ar.
   ========================================================================= */
(function () {
  'use strict';
  if (window.__lxNotifyLoaded) return;
  window.__lxNotifyLoaded = true;

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  /* ---- built-in labels (fallback when no server-rendered config blob) ------ */
  var STRINGS = {
    en: {
      title: 'Notify me',
      subtitle: 'Get notified when this product is back in stock',
      email_label: 'Email address',
      email_placeholder: 'you@email.com',
      email_error: 'Please enter a valid email address',
      whatsapp_label: 'WhatsApp number',
      phone_placeholder: '50 123 4567',
      phone_error: 'Please enter a valid phone number',
      country_label: 'Country code',
      news_optin: 'Notify me about other news or offers too',
      submit: 'Notify Me When Available',
      success_title: "You're on the list",
      success_body: "We'll message you the moment it's back in stock.",
      trigger: 'Notify me when available',
      close: 'Close'
    },
    ar: {
      title: 'نبّهني',
      subtitle: 'سنعلمك عند توفر هذا المنتج من جديد',
      email_label: 'البريد الإلكتروني',
      email_placeholder: 'you@email.com',
      email_error: 'يرجى إدخال بريد إلكتروني صحيح',
      whatsapp_label: 'رقم واتساب',
      phone_placeholder: '50 123 4567',
      phone_error: 'يرجى إدخال رقم هاتف صحيح',
      country_label: 'رمز الدولة',
      news_optin: 'أبلغني بالأخبار والعروض الأخرى أيضاً',
      submit: 'نبّهني عند التوفر',
      success_title: 'أنت مسجّل الآن',
      success_body: 'سنراسلك فور عودة المنتج للتوفر.',
      trigger: 'نبّهني عند التوفر',
      close: 'إغلاق'
    }
  };

  /* ---- country dial codes: default UAE, common GCC + a few majors ---------- */
  var COUNTRIES = [
    { code: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
    { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
    { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
    { code: '+974', flag: '🇶🇦', name: 'Qatar' },
    { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
    { code: '+968', flag: '🇴🇲', name: 'Oman' },
    { code: '+962', flag: '🇯🇴', name: 'Jordan' },
    { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
    { code: '+20', flag: '🇪🇬', name: 'Egypt' },
    { code: '+1', flag: '🇺🇸', name: 'United States / Canada' },
    { code: '+44', flag: '🇬🇧', name: 'United Kingdom' },
    { code: '+91', flag: '🇮🇳', name: 'India' },
    { code: '+92', flag: '🇵🇰', name: 'Pakistan' },
    { code: '+33', flag: '🇫🇷', name: 'France' },
    { code: '+49', flag: '🇩🇪', name: 'Germany' },
    { code: '+61', flag: '🇦🇺', name: 'Australia' }
  ];
  var DEFAULT_CODE = '+971';

  var ICONS = {
    close:
      '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1.5 1.5l11 11M12.5 1.5l-11 11"/></svg>',
    bell:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M18 8.4c0-3.2-2.7-5.9-6-5.9S6 5.2 6 8.4c0 6.9-3 8.1-3 8.1h18s-3-1.2-3-8.1Z"/><path d="M13.7 20.2a2 2 0 0 1-3.4 0"/></svg>',
    mail:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m4 7 8 5.5L20 7"/></svg>',
    chat:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.7L3 21l1.8-5.8A8.5 8.5 0 1 1 21 11.5Z"/></svg>',
    tick:
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 8.4 6.4 12 13 4.5"/></svg>',
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4 12.5 9.5 18 20 6"/></svg>'
  };

  /* ---- config + label resolution ------------------------------------------ */
  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s;
    var box = document.createElement('textarea');
    box.innerHTML = s;
    return box.value;
  }

  function isArabic() {
    var el = document.documentElement;
    return (el.getAttribute('dir') || '').toLowerCase() === 'rtl' || /^ar/i.test(el.lang || '');
  }

  var LABELS = null;
  var ENDPOINT = '';

  function resolveLabels() {
    if (LABELS) return LABELS;
    var base = isArabic() ? STRINGS.ar : STRINGS.en;
    LABELS = {};
    Object.keys(base).forEach(function (k) {
      LABELS[k] = base[k];
    });
    var el = document.querySelector('[data-lx-notify-config]');
    if (el) {
      ENDPOINT = el.getAttribute('data-lx-notify-endpoint') || '';
      try {
        var cfg = JSON.parse(el.textContent);
        if (cfg && typeof cfg.endpoint === 'string' && cfg.endpoint) ENDPOINT = cfg.endpoint;
        var i18n = (cfg && cfg.i18n) || {};
        Object.keys(i18n).forEach(function (k) {
          if (typeof i18n[k] === 'string' && i18n[k]) LABELS[k] = decodeEntities(i18n[k]);
        });
      } catch (e) {
        console.warn('[lx-notify] bad config JSON', e);
      }
    }
    return LABELS;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* "EU 40 | US 7 | UK 6" (or with slashes) -> "EU 40 · US 7 · UK 6" */
  function prettyVariant(title) {
    var t = String(title == null ? '' : title).trim();
    if (!t || /^default title$/i.test(t)) return '';
    return t.replace(/\s*[|/]\s*/g, ' · ');
  }

  /* ---- DOM (built once, reused) ------------------------------------------- */
  var dlg = null;
  var els = {};
  var closing = false;
  var opener = null;
  var ctx = {}; // current { productTitle, variantTitle, productId, variantId }
  var touched = { email: false, phone: false };

  function build() {
    if (dlg) return;
    var L = resolveLabels();

    var options = COUNTRIES.map(function (c) {
      var sel = c.code === DEFAULT_CODE ? ' selected' : '';
      return (
        '<option value="' + esc(c.code) + '"' + sel + '>' + esc(c.flag + ' ' + c.code) + '</option>'
      );
    }).join('');

    dlg = document.createElement('dialog');
    dlg.className = 'lx-nf';
    dlg.setAttribute('aria-labelledby', 'lx-nf-title');
    dlg.innerHTML =
      '<div class="lx-nf__panel" tabindex="-1" role="document">' +
      '<button type="button" class="lx-nf__grip" data-nf-grip aria-hidden="true"><span class="lx-nf__grip-pill"></span></button>' +
      '<button type="button" class="lx-nf__close" data-nf-close aria-label="' + esc(L.close) + '">' + ICONS.close + '</button>' +

      // ---- FORM VIEW ----
      '<div data-nf-form-view>' +
      '<div class="lx-nf__header">' +
      '<h2 class="lx-nf__title" id="lx-nf-title">' + esc(L.title) + '</h2>' +
      '<p class="lx-nf__subtitle">' + esc(L.subtitle) + '</p>' +
      '</div>' +
      '<div class="lx-nf__hair"></div>' +

      '<div class="lx-nf__variant">' +
      '<span class="lx-nf__product" data-nf-product></span>' +
      '<span class="lx-nf__chip" data-nf-chip></span>' +
      '</div>' +

      '<form data-nf-form novalidate>' +
      // email
      '<div class="lx-nf__field">' +
      '<label class="lx-nf__label" for="lx-nf-email">' + esc(L.email_label) +
      '<span class="lx-nf__req" aria-hidden="true">*</span></label>' +
      '<div class="lx-nf__input-wrap">' +
      '<span class="lx-nf__input-icon">' + ICONS.mail + '</span>' +
      '<input class="lx-nf__input" data-nf-email id="lx-nf-email" type="email" inputmode="email" ' +
      'autocomplete="email" required placeholder="' + esc(L.email_placeholder) + '" ' +
      'aria-describedby="lx-nf-email-err">' +
      '</div>' +
      '<p class="lx-nf__error" data-nf-email-err id="lx-nf-email-err" hidden>' + esc(L.email_error) + '</p>' +
      '</div>' +
      // whatsapp
      '<div class="lx-nf__field">' +
      '<label class="lx-nf__label" for="lx-nf-phone">' + esc(L.whatsapp_label) +
      '<span class="lx-nf__req" aria-hidden="true">*</span></label>' +
      '<div class="lx-nf__phone-row">' +
      '<span class="lx-nf__country">' +
      '<select class="lx-nf__country-select" data-nf-country aria-label="' + esc(L.country_label) + '">' +
      options +
      '</select>' +
      '</span>' +
      '<span class="lx-nf__phone-wrap">' +
      '<span class="lx-nf__phone-icon">' + ICONS.chat + '</span>' +
      '<input class="lx-nf__phone" data-nf-phone id="lx-nf-phone" type="tel" inputmode="tel" ' +
      'autocomplete="tel-national" required placeholder="' + esc(L.phone_placeholder) + '" ' +
      'aria-describedby="lx-nf-phone-err">' +
      '</span>' +
      '</div>' +
      '<p class="lx-nf__error" data-nf-phone-err id="lx-nf-phone-err" hidden>' + esc(L.phone_error) + '</p>' +
      '</div>' +
      // opt-in
      '<label class="lx-nf__optin">' +
      '<input type="checkbox" data-nf-optin checked>' +
      '<span class="lx-nf__check">' + ICONS.tick + '</span>' +
      '<span class="lx-nf__optin-text">' + esc(L.news_optin) + '</span>' +
      '</label>' +
      // submit
      '<button type="submit" class="lx-nf__submit" data-nf-submit disabled>' +
      '<span data-nf-submit-text>' + esc(L.submit) + '</span>' +
      '</button>' +
      '</form>' +
      '</div>' +

      // ---- SUCCESS VIEW ----
      '<div class="lx-nf__success" data-nf-success-view hidden>' +
      '<span class="lx-nf__success-badge">' + ICONS.check + '</span>' +
      '<h2 class="lx-nf__success-title">' + esc(L.success_title) + '</h2>' +
      '<p class="lx-nf__success-body">' + esc(L.success_body) + '</p>' +
      '</div>' +

      '</div>';

    document.body.appendChild(dlg);

    // collect data-nf-* hooks
    els = {};
    dlg.querySelectorAll('*').forEach(function (el) {
      for (var i = 0; i < el.attributes.length; i++) {
        var name = el.attributes[i].name;
        if (name.indexOf('data-nf-') === 0) {
          var key = name.slice(8).replace(/-([a-z])/g, function (m, c) {
            return c.toUpperCase();
          });
          els[key] = el;
        }
      }
    });
    els.panel = dlg.querySelector('.lx-nf__panel');

    wire();
  }

  /* ---- validation --------------------------------------------------------- */
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function emailValid() {
    return EMAIL_RE.test((els.email.value || '').trim());
  }
  function phoneDigits() {
    return (els.phone.value || '').replace(/\D+/g, '');
  }
  function phoneValid() {
    var d = phoneDigits();
    return d.length >= 6 && d.length <= 15;
  }
  function bothValid() {
    return emailValid() && phoneValid();
  }
  function refreshSubmit() {
    els.submit.disabled = !bothValid();
  }
  function showFieldError(which) {
    if (which === 'email') {
      els.email.classList.add('is-invalid');
      els.emailErr.hidden = false;
    } else {
      els.phone.classList.add('is-invalid');
      els.phoneErr.hidden = false;
    }
  }
  function clearFieldError(which) {
    if (which === 'email') {
      els.email.classList.remove('is-invalid');
      els.emailErr.hidden = true;
    } else {
      els.phone.classList.remove('is-invalid');
      els.phoneErr.hidden = true;
    }
  }

  /* ---- wiring (once) ------------------------------------------------------ */
  function wire() {
    els.close.addEventListener('click', function () {
      hide();
    });
    dlg.addEventListener('cancel', function (e) {
      e.preventDefault(); // route Esc through our animated close
      hide();
    });

    // backdrop close, drag-safe (pointerDOWN must also land on the wash)
    var downOnBackdrop = false;
    dlg.addEventListener(
      'pointerdown',
      function (e) {
        downOnBackdrop = e.target === dlg;
      },
      true
    );
    dlg.addEventListener('click', function (e) {
      if (e.target === dlg && downOnBackdrop) hide();
      downOnBackdrop = false;
    });

    // live validity → enable/disable submit; clear an error once it's fixed
    els.email.addEventListener('input', function () {
      if (touched.email && emailValid()) clearFieldError('email');
      refreshSubmit();
    });
    els.phone.addEventListener('input', function () {
      if (touched.phone && phoneValid()) clearFieldError('phone');
      refreshSubmit();
    });
    els.email.addEventListener('blur', function () {
      touched.email = true;
      if (!emailValid() && els.email.value.trim()) showFieldError('email');
      else clearFieldError('email');
    });
    els.phone.addEventListener('blur', function () {
      touched.phone = true;
      if (!phoneValid() && els.phone.value.trim()) showFieldError('phone');
      else clearFieldError('phone');
    });

    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit();
    });

    // mobile grip drag-to-close
    var dragY = null;
    els.grip.addEventListener('pointerdown', function (e) {
      dragY = e.clientY;
      els.panel.style.transition = 'none';
      els.grip.setPointerCapture(e.pointerId);
    });
    els.grip.addEventListener('pointermove', function (e) {
      if (dragY == null) return;
      var dy = e.clientY - dragY;
      els.panel.style.transform = 'translateY(' + (dy > 0 ? dy : dy * 0.15) + 'px)';
    });
    function endDrag(e) {
      if (dragY == null) return;
      var dy = e.clientY - dragY;
      dragY = null;
      els.panel.style.transition = '';
      els.panel.style.transform = '';
      if (dy > 110) hide();
    }
    els.grip.addEventListener('pointerup', endDrag);
    els.grip.addEventListener('pointercancel', endDrag);
  }

  /* ---- submit (STUB backend) ---------------------------------------------- */
  function submit() {
    // final gate — never submit an invalid form
    if (!emailValid()) {
      touched.email = true;
      showFieldError('email');
      els.email.focus();
      return;
    }
    if (!phoneValid()) {
      touched.phone = true;
      showFieldError('phone');
      els.phone.focus();
      return;
    }

    var payload = {
      email: (els.email.value || '').trim(),
      phone: phoneDigits(),
      countryCode: els.country.value || DEFAULT_CODE,
      productId: ctx.productId != null ? ctx.productId : null,
      variantId: ctx.variantId != null ? ctx.variantId : null,
      variantTitle: ctx.variantTitle || '',
      newsOptin: !!els.optin.checked
    };

    if (!ENDPOINT) {
      // Documented STUB: no backend yet. Do NOT persist; complete the visual
      // flow so the modal is demoable pending the owner's real integration.
      console.warn('[lx-notify] no endpoint configured — submission not persisted', payload);
      showSuccess();
      return;
    }

    els.submit.classList.add('is-busy');
    els.submit.disabled = true;
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        showSuccess();
      })
      .catch(function (err) {
        console.warn('[lx-notify] submit failed', err);
        // keep the shopper's data on screen; let them retry
        els.submit.classList.remove('is-busy');
        els.submit.disabled = false;
      });
  }

  function showSuccess() {
    els.submit.classList.remove('is-busy');
    els.formView.hidden = true;
    els.successView.hidden = false;
    try {
      els.close.focus({ preventScroll: true });
    } catch (e) {
      /* noop */
    }
  }

  function resetForm() {
    els.formView.hidden = false;
    els.successView.hidden = true;
    els.form.reset(); // restores optin=checked (default) + clears fields
    touched.email = false;
    touched.phone = false;
    clearFieldError('email');
    clearFieldError('phone');
    els.submit.classList.remove('is-busy');
    refreshSubmit();
  }

  /* ---- open / close ------------------------------------------------------- */
  function open(data) {
    build();
    ctx = data || {};

    els.product.textContent = ctx.productTitle || '';
    var chip = prettyVariant(ctx.variantTitle);
    els.chip.textContent = chip;
    resetForm();

    opener = document.activeElement;
    document.body.classList.add('overflow-hidden');
    if (typeof dlg.showModal === 'function') {
      if (!dlg.open) dlg.showModal();
    } else {
      dlg.setAttribute('open', '');
    }

    if (REDUCED.matches) {
      dlg.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          dlg.classList.add('is-open');
        });
      });
    }

    // focus the first field (desktop); on small screens focus the panel so the
    // keyboard doesn't jump up before the sheet settles
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 600px)').matches) {
        els.panel.focus({ preventScroll: true });
      } else {
        els.email.focus({ preventScroll: true });
      }
    } catch (e) {
      /* noop */
    }
  }

  function hide() {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    dlg.classList.remove('is-open');
    dlg.classList.add('is-closing');
    var back = opener;
    var done = function () {
      dlg.classList.remove('is-closing');
      closing = false;
      if (dlg.open) dlg.close();
      document.body.classList.remove('overflow-hidden');
      if (back && document.contains(back)) {
        try {
          back.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
    };
    if (REDUCED.matches) done();
    else setTimeout(done, 260);
  }

  window.LxNotify = { open: open, close: hide };
})();
